import { motion } from "framer-motion"
import {
  BadgePercent,
  Calculator,
  ClipboardCheck,
  FileDown,
  MapPin,
  Package,
  Receipt,
  SlidersHorizontal,
} from "lucide-react"
import MaterialsQuotationWelcomeBlock from "../MaterialsQuotationWelcomeBlock.tsx"
import styles from "../MaterialsQuotationWelcome.module.scss"
import calcStyles from "../CalculadoraDeMateriales.module.scss"
import { getMaterialsCatalogPricesUpdatedLabel } from "../../../../constants/calculadoraDeMateriales.ts"

type CalculadoraIntegratedWelcomeProps = {
  showIntegratedEstimationWelcome: boolean
  totalCatalogMaterialsLabel: string
  onNavigateToCostEstimation: () => void
  onBeginManual: () => void
  onFileChosen: (file: File) => void
}

const CalculadoraIntegratedWelcome = ({
  showIntegratedEstimationWelcome,
  totalCatalogMaterialsLabel,
  onNavigateToCostEstimation,
  onBeginManual,
  onFileChosen,
}: CalculadoraIntegratedWelcomeProps) => {
  return (
    <div className={calcStyles.container_welcomeFull}>
      <div className={calcStyles.container_welcomeFormSlot}>
        <div className={styles.formContainer}>
          <motion.div
            className={styles.welcomeShell}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <MaterialsQuotationWelcomeBlock
              isLoading={false}
              onBeginManual={onBeginManual}
              onFileChosen={onFileChosen}
              onGoToCostEstimation={showIntegratedEstimationWelcome ? undefined : onNavigateToCostEstimation}
              title={
                showIntegratedEstimationWelcome
                  ? "Calculá el costo de materiales de tu obra"
                  : "Publicá tu lista y recibí presupuestos"
              }
              description={
                showIntegratedEstimationWelcome
                  ? "Subí tu lista o buscá por rubro y obtené una referencia de precios en minutos."
                  : "Armá tu listado y recibí ofertas de proveedores de tu zona."
              }
              highlights={
                showIntegratedEstimationWelcome
                  ? [
                      { icon: BadgePercent, label: getMaterialsCatalogPricesUpdatedLabel() },
                      { icon: Calculator, label: "Referencia de costos en minutos" },
                      { icon: SlidersHorizontal, label: "Ajustá cantidades en cualquier momento" },
                      { icon: FileDown, label: "Descargá tu estimación en PDF" },
                    ]
                  : [
                      { icon: Package, label: `Catálogo con ${totalCatalogMaterialsLabel} materiales` },
                      { icon: MapPin, label: "Notificamos proveedores de tu zona" },
                      { icon: Receipt, label: "Recibí presupuestos en Edify" },
                      { icon: ClipboardCheck, label: "Ajustá la lista antes de publicar" },
                    ]
              }
              assistedDescription={
                showIntegratedEstimationWelcome
                  ? "Subí tu Excel, PDF o Word y la IA detecta materiales y cantidades automáticamente. Ideal si ya tenés un presupuesto o listado armado."
                  : "Subí tu Excel, PDF o Word y la IA detecta materiales y cantidades automáticamente. Ideal si ya tenés un listado armado."
              }
              assistedBullets={
                showIntegratedEstimationWelcome
                  ? [
                      "Detectamos materiales y cantidades del archivo",
                      "Revisás y ajustás la lista antes de estimar",
                      "Descargás tu estimación con un clic",
                    ]
                  : [
                      "Detectamos materiales y cantidades del archivo",
                      "Revisás y ajustás la lista antes de publicar",
                      "Publicás y recibís presupuestos de proveedores",
                    ]
              }
              manualDescription={
                showIntegratedEstimationWelcome
                  ? "Buscá materiales por nombre o explorá por rubro y armá tu lista desde cero."
                  : "Buscá materiales por nombre o explorá por rubro y armá tu solicitud desde cero."
              }
              manualDescriptionCompact={
                showIntegratedEstimationWelcome
                  ? "Buscá por nombre o rubro y armá tu lista."
                  : "Buscá por nombre o rubro y armá tu solicitud."
              }
              manualSteps={
                showIntegratedEstimationWelcome
                  ? [
                      "Buscás por nombre o explorás por rubro.",
                      "Agregás materiales y ajustás cantidades.",
                      "Revisás totales y referencias de precio.",
                      "Descargás tu estimación en PDF.",
                    ]
                  : [
                      "Buscás por nombre o explorás por rubro.",
                      "Agregás materiales y ajustás cantidades.",
                      "Revisás tu lista antes de publicar.",
                      "Publicás y recibís presupuestos de proveedores.",
                    ]
              }
              manualCta="Empezar manualmente"
              recommendedFirst
            />
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default CalculadoraIntegratedWelcome
